import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { MessageDescriptor } from "@lingui/core";

import { getAnalyticsAdapter } from "../../analytics/AnalyticsAdapter";
import { useGameStore } from "../../state/store";
import { useTutorialStore } from "../../state/tutorialStore";
import { Button } from "../common/Button";
import { computeBubblePosition, type BubblePos } from "./bubblePosition";
import { CoachmarkBubble } from "./CoachmarkBubble";
import { Spotlight } from "./Spotlight";
import { TUTORIAL_STEPS, TUTORIAL_TOTAL, isSkipAvailable, type TutorialPhase } from "./tutorialSteps";
import { useTargetRect } from "./useTargetRect";
import styles from "./TutorialOverlay.module.css";

const PHASE_LABEL: Record<TutorialPhase, MessageDescriptor> = {
  dashboard: msg`Знакомство`,
  contract: msg`Берём контракт`,
  logs: msg`Разбор логов`,
  mail: msg`Разбор почты`,
  report: msg`Сборка отчёта`,
  done: msg`Готово`,
};

const HOLE_PADDING = 8;
const FLASH_DURATION_MS = 1500;
const FAREWELL_MS = 1800;

function skillsSumOf(skills: Record<string, number>): number {
  return Object.values(skills).reduce((sum, value) => sum + value, 0);
}

export function TutorialOverlay(): JSX.Element | null {
  const { i18n } = useLingui();
  const status = useTutorialStore((s) => s.status);
  const stepIndex = useTutorialStore((s) => s.stepIndex);
  const goTo = useTutorialStore((s) => s.goTo);
  const skip = useTutorialStore((s) => s.skip);
  const complete = useTutorialStore((s) => s.complete);

  const currentScreen = useGameStore((s) => s.currentScreen);
  const navigateTo = useGameStore((s) => s.navigateTo);
  const skillsSum = useGameStore((s) => skillsSumOf(s.player.skills));
  const ownedToolsLen = useGameStore((s) => s.player.ownedTools.length);

  const [skipOpen, setSkipOpen] = useState(false);
  const [farewell, setFarewell] = useState<"completed" | "skipped" | null>(null);
  const [dimActive, setDimActive] = useState(true);
  const [bubblePos, setBubblePos] = useState<({ step: number } & BubblePos) | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  const step = TUTORIAL_STEPS[stepIndex] ?? null;
  const onTargetScreen = Boolean(step && currentScreen === step.screen);
  const active = status === "in_progress" && currentScreen !== "main_menu" && onTargetScreen;
  const mainRect = useTargetRect(active && step ? step.targetId : null);
  const doneRect = useTargetRect(active && step?.done ? step.done.targetId : null);
  const doneActive = Boolean(step?.done && doneRect);
  const rect = doneActive ? doneRect : mainRect;
  const pointerSide = doneActive ? "top" : (step?.pointerSide ?? "bottom");
  const isInfoStep = step?.advance.kind === "next";
  const skipAllowed = isSkipAvailable(stepIndex);

  useLayoutEffect(() => {
    if (!active || farewell) {
      return;
    }
    const el = bubbleRef.current;
    if (!el) {
      return;
    }
    if (step?.targetId && !rect && !isInfoStep) {
      return;
    }
    const next = computeBubblePosition(rect, pointerSide, el.offsetWidth, el.offsetHeight, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    setBubblePos((prev) =>
      prev && prev.step === stepIndex && prev.top === next.top && prev.left === next.left
        ? prev
        : { step: stepIndex, ...next },
    );
  }, [active, farewell, rect, stepIndex, pointerSide, step, isInfoStep, doneActive]);

  const baselineRef = useRef<{ step: number; skills: number; tools: number } | null>(null);
  const trackedRef = useRef<number>(-1);

  useEffect(() => {
    if (!active) {
      return;
    }
    if (trackedRef.current !== stepIndex) {
      trackedRef.current = stepIndex;
      getAnalyticsAdapter().track("tutorial_step_viewed", { step: stepIndex + 1 });
    }
  }, [active, stepIndex]);

  useEffect(() => {
    if (!active) {
      return;
    }
    setDimActive(true);
    const timer = window.setTimeout(() => setDimActive(false), FLASH_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [active, stepIndex]);

  useEffect(() => {
    if (status !== "in_progress" || !step) {
      return;
    }
    if (step.advance.kind === "navigate" && currentScreen === step.advance.to) {
      goTo(stepIndex + 1);
    }
  }, [status, step, currentScreen, stepIndex, goTo]);

  useEffect(() => {
    if (status !== "in_progress" || !step || !onTargetScreen) {
      return;
    }
    if (!baselineRef.current || baselineRef.current.step !== stepIndex) {
      baselineRef.current = { step: stepIndex, skills: skillsSum, tools: ownedToolsLen };
      return;
    }
    if (step.advance.kind === "skillUpgraded" && skillsSum > baselineRef.current.skills) {
      goTo(stepIndex + 1);
    } else if (step.advance.kind === "toolPurchased" && ownedToolsLen > baselineRef.current.tools) {
      goTo(stepIndex + 1);
    }
  }, [status, step, onTargetScreen, stepIndex, skillsSum, ownedToolsLen, goTo]);

  useEffect(() => {
    if (!active || rect || farewell || isInfoStep) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (stepIndex + 1 < TUTORIAL_TOTAL) {
        goTo(stepIndex + 1);
      } else {
        complete();
        setFarewell("completed");
      }
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [active, rect, farewell, stepIndex, goTo, isInfoStep, complete]);

  const handleComplete = useCallback(() => {
    getAnalyticsAdapter().track("tutorial_completed");
    complete();
    setFarewell("completed");
    navigateTo("dashboard");
  }, [complete, navigateTo]);

  const handleNext = useCallback(() => {
    if (!step) {
      return;
    }
    if (step.final) {
      handleComplete();
    } else {
      goTo(stepIndex + 1);
    }
  }, [step, stepIndex, goTo, handleComplete]);

  const handleSkipRequest = useCallback(() => {
    if (step?.final) {
      handleComplete();
      return;
    }
    if (!skipAllowed) {
      return;
    }
    setSkipOpen(true);
  }, [step, handleComplete, skipAllowed]);

  const handleSkipConfirm = useCallback(() => {
    getAnalyticsAdapter().track("tutorial_skipped", { step: stepIndex + 1 });
    setSkipOpen(false);
    skip();
    setFarewell("skipped");
  }, [stepIndex, skip]);

  useEffect(() => {
    if (farewell === null) {
      return;
    }
    const timer = window.setTimeout(() => setFarewell(null), FAREWELL_MS);
    return () => window.clearTimeout(timer);
  }, [farewell]);

  useEffect(() => {
    if (status !== "in_progress" || farewell) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") {
        return;
      }
      if (step?.final) {
        handleComplete();
        return;
      }
      if (!skipAllowed) {
        return;
      }
      setSkipOpen((open) => !open);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [status, farewell, step, handleComplete, skipAllowed]);

  const buttonLabel = useMemo(() => {
    if (!step || step.advance.kind !== "next") {
      return null;
    }
    return step.final ? i18n._(msg`К следующему контракту`) : i18n._(msg`Далее`);
  }, [step, i18n]);

  if (farewell) {
    return (
      <div className={styles.toastLayer} role="status" aria-live="polite">
        <div className={styles.toast}>
          {farewell === "completed" ? (
            <Trans>Обучение пройдено</Trans>
          ) : (
            <Trans>Обучение пропущено - вернуться можно в настройках</Trans>
          )}
        </div>
      </div>
    );
  }

  if (status !== "in_progress" || currentScreen === "main_menu" || !step || !active) {
    return null;
  }

  const stepNumber = stepIndex + 1;
  return (
    <div className={styles.layer}>
      <Spotlight rect={rect} padding={HOLE_PADDING} dimActive={dimActive} />

      <CoachmarkBubble
        stepNumber={stepNumber}
        total={TUTORIAL_TOTAL}
        phaseLabel={i18n._(PHASE_LABEL[step.phase])}
        title={i18n._(doneActive && step.done ? step.done.title : step.title)}
        body={i18n._(doneActive && step.done ? step.done.body : step.body)}
        buttonLabel={buttonLabel}
        style={{
          top: bubblePos?.step === stepIndex ? bubblePos.top : undefined,
          left: bubblePos?.step === stepIndex ? bubblePos.left : undefined,
          visibility: bubblePos?.step === stepIndex ? "visible" : "hidden",
        }}
        bubbleRef={bubbleRef}
        onNext={handleNext}
        onSkip={skipAllowed ? handleSkipRequest : undefined}
      />

      <div className={styles.stepper}>
        <span className={styles.stepperText}>
          <Trans>
            Шаг {stepNumber} из {TUTORIAL_TOTAL}
          </Trans>
        </span>
        <span className={styles.dots} aria-hidden="true">
          {TUTORIAL_STEPS.map((_, index) => (
            <span
              key={index}
              className={
                index < stepIndex
                  ? styles.dotDone
                  : index === stepIndex
                    ? styles.dotCurrent
                    : styles.dot
              }
            />
          ))}
        </span>
      </div>

      {skipOpen && (
        <div className={styles.modalScrim} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>
              <Trans>Пропустить обучение?</Trans>
            </h3>
            <p className={styles.modalBody}>
              <Trans>Можно вернуться к нему позже в настройках.</Trans>
            </p>
            <div className={styles.modalActions}>
              <Button variant="ghost" size="small" onClick={() => setSkipOpen(false)}>
                <Trans>Продолжить обучение</Trans>
              </Button>
              <Button variant="danger" size="small" onClick={handleSkipConfirm}>
                <Trans>Пропустить</Trans>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
